// ============================================================================
// Roof Designer · RoofDesignerWorkspace — Komposition + API-Persistenz
// ----------------------------------------------------------------------------
// Layout (final): kompakter Header (Titel, Version/Save-State, 3 Status-Chips,
// Primäraktionen) → Workflow-Stepbar → Import-Karte → Toolbar → Canvas + rechtes
// Ergebnis-/Eigenschaften-Panel.
// Persistenz-Stufe 3: API-first (per Projekt), localStorage nur als Fallback.
// Spar-Invariante: canApprove immer false, requiresHumanReview immer true.
// ============================================================================
import { useReducer, useMemo, useEffect, useRef, useCallback, useState } from "react";
import {
  designerReducer,
  initialDesignerState,
  saveDesignerState,
  loadDesignerState,
} from "./state.js";
import { computeDesignerResult, exportDesignSummary } from "./roof-engine.js";
import type { DesignSummary } from "./roof-engine.js";
import { designerStateFromRoofSurface } from "./import-surface.js";
import type { RoofSurfaceRecord } from "./import-surface.js";
import { Toolbar } from "./components/Toolbar.js";
import { DesignerCanvas } from "./components/DesignerCanvas.js";
import { PropertiesPanel } from "./components/PropertiesPanel.js";
import { ResultPanel } from "./components/ResultPanel.js";
import { StepBar } from "./components/StepBar.js";
import { ImportCard } from "./components/ImportCard.js";
import type { DesignerState } from "./types.js";
import "./roofdesigner.css";

// ─── API-Typen ────────────────────────────────────────────────────────────────

interface RoofDesignRecord {
  id: string;
  tenantId: string;
  projectId: string;
  status: "draft" | "needs_review" | "qa_ready";
  version: number;
  name: string;
  designJson: Record<string, unknown>;
  summaryJson: Record<string, unknown>;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

type SaveState = "unsaved" | "saving" | "saved" | "error";

// Antwort von GET /projects/:id/geospatial (nur die hier benötigten Felder).
interface GeoBuildingRecord {
  id: string;
  buildingType: string | null;
  roofSurfaces: RoofSurfaceRecord[];
}

// Discriminated union — vermeidet exactOptionalPropertyTypes-Konflikte
type ApiFetchResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string };

// ─── API-Hilfsfunktionen ──────────────────────────────────────────────────────

function getAuthToken(): string | null {
  // Liest den persistierten Auth-Store (zustand/persist, Key "energyos-auth") —
  // identisch zu lib/api-client.ts. Der frühere Key "energyos_token" existierte
  // nirgends → alle Designer-API-Calls liefen als stille 401s.
  try {
    const stored = localStorage.getItem("energyos-auth");
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { state?: { user?: { token?: string } } };
    return parsed.state?.user?.token ?? null;
  } catch {
    return null;
  }
}

async function apiFetch<T = unknown>(
  path: string,
  opts?: RequestInit,
): Promise<ApiFetchResult<T>> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts?.headers != null ? (opts.headers as Record<string, string>) : {}),
  };
  try {
    const res = await fetch(`/api${path}`, { ...opts, headers });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    }
    const json = await res.json() as { data: T };
    return { ok: true, data: json.data };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function downloadDesignSummary(summary: DesignSummary) {
  const json = JSON.stringify(summary, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dach-design-entwurf-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  projectId?: string;
}

// ─── Outer Wrapper — verwaltet forceKey für Remount bei API-Load ──────────────

export function RoofDesignerWorkspace({ projectId }: Props) {
  const storageKey = projectId ?? "default";

  // forceKey steuert InnerWorkspace-Remount nach API-Load
  const [forceKey, setForceKey]                         = useState(0);
  const [savedDesignId, setSavedDesignId]               = useState<string | null>(null);
  const [savedDesignVersion, setSavedDesignVersion]     = useState<number | null>(null);
  const [savedDesignStatus, setSavedDesignStatus]       = useState<string | null>(null);
  const [saveState, setSaveState]                       = useState<SaveState>("unsaved");
  const [apiLoadError, setApiLoadError]                 = useState<string | null>(null);
  const [initialLoadSuccessMsg, setInitialLoadSuccessMsg] = useState<string | null>(null);

  const handleLoadFromApi = useCallback(async () => {
    if (projectId == null) return;
    setApiLoadError(null);
    const res = await apiFetch<RoofDesignRecord[]>(
      `/projects/${projectId}/roof-designs`,
    );
    if (!res.ok) {
      setApiLoadError(res.error);
      return;
    }
    const list = res.data;
    if (list.length === 0) {
      setApiLoadError("Kein gespeicherter Entwurf gefunden.");
      return;
    }
    const latestMeta = list[0];
    if (latestMeta == null) {
      setApiLoadError("Kein gespeicherter Entwurf gefunden.");
      return;
    }
    // Der List-Endpoint liefert nur Metadaten (kein designJson) — die Geometrie
    // kommt ausschließlich vom Einzel-Endpoint.
    const full = await apiFetch<RoofDesignRecord>(
      `/projects/${projectId}/roof-designs/${latestMeta.id}`,
    );
    if (!full.ok) {
      setApiLoadError(full.error);
      return;
    }
    const latest = full.data;
    setSavedDesignId(latest.id);
    setSavedDesignVersion(latest.version);
    setSavedDesignStatus(latest.status);
    setSaveState("saved");
    setInitialLoadSuccessMsg(`Entwurf Version ${latest.version} geladen ✓`);
    const savedState = latest.designJson as unknown as DesignerState;
    if (savedState != null && typeof savedState === "object" && "polygon" in savedState) {
      // API-Zustand in localStorage schreiben → InnerWorkspace liest ihn beim Remount
      saveDesignerState(storageKey, savedState);
    }
    // Remount InnerWorkspace mit frischem localStorage-Stand
    setForceKey((k) => k + 1);
  }, [projectId, storageKey]);

  // Beim Mount: neuesten Entwurf von der API laden (einmalig, wenn projectId gesetzt)
  useEffect(() => {
    if (projectId == null) return;
    void (async () => {
      const res = await apiFetch<RoofDesignRecord[]>(
        `/projects/${projectId}/roof-designs`,
      );
      if (!res.ok || res.data.length === 0) return;
      const latestMeta = res.data[0];
      if (latestMeta == null) return;
      // designJson kommt nur vom Einzel-Endpoint (List = Metadaten ohne Geometrie).
      const full = await apiFetch<RoofDesignRecord>(
        `/projects/${projectId}/roof-designs/${latestMeta.id}`,
      );
      if (!full.ok) return;
      const latest = full.data;
      setSavedDesignId(latest.id);
      setSavedDesignVersion(latest.version);
      setSavedDesignStatus(latest.status);
      setSaveState("saved");
      const savedState = latest.designJson as unknown as DesignerState;
      if (savedState != null && typeof savedState === "object" && "polygon" in savedState) {
        saveDesignerState(storageKey, savedState);
        setForceKey((k) => k + 1);
      }
    })();
    // Nur einmal beim Mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <InnerWorkspace
      key={forceKey}
      projectId={projectId}
      storageKey={storageKey}
      savedDesignId={savedDesignId}
      setSavedDesignId={setSavedDesignId}
      savedDesignVersion={savedDesignVersion}
      setSavedDesignVersion={setSavedDesignVersion}
      savedDesignStatus={savedDesignStatus}
      setSavedDesignStatus={setSavedDesignStatus}
      saveState={saveState}
      setSaveState={setSaveState}
      apiLoadError={apiLoadError}
      setApiLoadError={setApiLoadError}
      onLoadFromApi={handleLoadFromApi}
      initialLoadSuccessMsg={initialLoadSuccessMsg}
      onClearInitialLoadSuccessMsg={() => setInitialLoadSuccessMsg(null)}
    />
  );
}

// ─── InnerProps — explizit keine optionalen Felder (exactOptionalPropertyTypes) ─

interface InnerProps {
  projectId: string | undefined;
  storageKey: string;
  savedDesignId: string | null;
  setSavedDesignId: (id: string | null) => void;
  savedDesignVersion: number | null;
  setSavedDesignVersion: (v: number | null) => void;
  savedDesignStatus: string | null;
  setSavedDesignStatus: (s: string | null) => void;
  saveState: SaveState;
  setSaveState: (s: SaveState) => void;
  apiLoadError: string | null;
  setApiLoadError: (e: string | null) => void;
  onLoadFromApi: () => Promise<void>;
  initialLoadSuccessMsg: string | null;
  onClearInitialLoadSuccessMsg: () => void;
}

// ─── InnerWorkspace (kann remounten wenn forceKey sich ändert) ────────────────

function InnerWorkspace({
  projectId,
  storageKey,
  savedDesignId,
  setSavedDesignId,
  savedDesignVersion,
  setSavedDesignVersion,
  savedDesignStatus,
  setSavedDesignStatus,
  saveState,
  setSaveState,
  apiLoadError,
  setApiLoadError,
  onLoadFromApi,
  initialLoadSuccessMsg,
  onClearInitialLoadSuccessMsg,
}: InnerProps) {
  const [loadSuccessMsg, setLoadSuccessMsg] = useState<string | null>(initialLoadSuccessMsg);

  // Temporäre Anzeige der Load-Success-Meldung (3 Sekunden)
  useEffect(() => {
    if (initialLoadSuccessMsg == null) return;
    const t = setTimeout(() => {
      setLoadSuccessMsg(null);
      onClearInitialLoadSuccessMsg();
    }, 3000);
    return () => { clearTimeout(t); };
    // Nur beim ersten Mount reagieren
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [state, dispatch] = useReducer(
    designerReducer,
    undefined,
    () => loadDesignerState(storageKey) ?? initialDesignerState(),
  );
  const result = useMemo(() => computeDesignerResult(state), [state]);

  // Markiert als "unsaved" bei jeder Zustandsänderung
  const prevStateRef = useRef<DesignerState>(state);
  useEffect(() => {
    if (prevStateRef.current !== state) {
      setSaveState("unsaved");
      prevStateRef.current = state;
    }
  }, [state, setSaveState]);

  // Debounced localStorage-Persistenz (500 ms nach letzter Änderung)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveDesignerState(storageKey, state);
    }, 500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [state, storageKey]);

  const hasPolygon = state.polygon.length >= 3 && state.polygonClosed;

  // ── Gebäude-Dachflächen (RoofSurface → RoofDesign Import) ──────────────────
  const [availableSurfaces, setAvailableSurfaces] = useState<RoofSurfaceRecord[]>([]);
  const [selectedSurfaceId, setSelectedSurfaceId] = useState<string>("");

  useEffect(() => {
    if (projectId == null) return;
    void (async () => {
      const res = await apiFetch<{ buildings: GeoBuildingRecord[] }>(
        `/projects/${projectId}/geospatial`,
      );
      if (!res.ok) return; // kein Gebäude erfasst / Fehler → Import-UI bleibt leer
      const surfaces = res.data.buildings.flatMap((b) =>
        b.roofSurfaces.map((s) => ({ ...s, buildingId: b.id })),
      );
      setAvailableSurfaces(surfaces);
    })();
  }, [projectId]);

  const handleImportSurface = useCallback(() => {
    const surface = availableSurfaces.find((s) => s.id === selectedSurfaceId);
    if (!surface) return;
    const imported = designerStateFromRoofSurface(surface);
    dispatch({ type: "IMPORT_SURFACE_STATE", state: imported });
  }, [availableSurfaces, selectedSurfaceId]);

  // ── API-Speichern ──────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (projectId == null) return;
    setSaveState("saving");
    setApiLoadError(null);

    const summary = exportDesignSummary(state, result);
    const body = JSON.stringify({
      name: state.importSource != null
        ? `Dachentwurf — ${state.importSource.label}`.slice(0, 120)
        : "Dachentwurf",
      status: "draft",
      designJson: state as unknown as Record<string, unknown>,
      summaryJson: summary as unknown as Record<string, unknown>,
    });

    if (savedDesignId != null) {
      const res = await apiFetch<RoofDesignRecord>(
        `/projects/${projectId}/roof-designs/${savedDesignId}`,
        { method: "PUT", body },
      );
      if (res.ok) {
        // Badge sofort aus der API-Antwort aktualisieren (Version inkrementiert
        // serverseitig, Status kann sich durch den Save ändern, z. B. → draft).
        setSavedDesignVersion(res.data.version);
        setSavedDesignStatus(res.data.status);
        setSaveState("saved");
      } else {
        setSaveState("error");
        setApiLoadError(res.error);
      }
    } else {
      const res = await apiFetch<RoofDesignRecord>(
        `/projects/${projectId}/roof-designs`,
        { method: "POST", body },
      );
      if (res.ok) {
        setSavedDesignId(res.data.id);
        setSavedDesignVersion(res.data.version);
        setSavedDesignStatus(res.data.status);
        setSaveState("saved");
      } else {
        setSaveState("error");
        setApiLoadError(res.error);
      }
    }
  }, [projectId, state, result, savedDesignId, setSaveState, setSavedDesignId, setSavedDesignVersion, setSavedDesignStatus, setApiLoadError]);

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (!hasPolygon) return;
    const summary = exportDesignSummary(state, result);
    downloadDesignSummary(summary);
  }, [state, result, hasPolygon]);

  const handleClearSaved = useCallback(() => {
    // localStorage löschen, aber API-Entwurf behalten
    try { localStorage.removeItem(`energyos-roof-design-${storageKey}`); } catch {}
  }, [storageKey]);

  // ── Save-State-Anzeige ────────────────────────────────────────────────────
  const saveLabel =
    saveState === "saving" ? "Speichern…"
    : saveState === "saved"   ? (savedDesignVersion != null ? `Gespeichert · v${savedDesignVersion}` : "Gespeichert")
    : saveState === "error"   ? "Fehler beim Speichern"
    : "Ungespeicherte Änderungen";
  const saveDotClass =
    saveState === "saved"   ? "rd-savedot--ok"
    : saveState === "error"  ? "rd-savedot--err"
    : saveState === "saving" ? "rd-savedot--busy"
    : "rd-savedot--dirty";

  const satelliteActive = state.backgroundGeoRef != null && state.backgroundGeoRef.enabled;

  return (
    <div className="rd-root" data-project-id={projectId ?? ""}>
      {/* ── Header: kompakte Info + Status + Primäraktionen ── */}
      <div className="rd-header" data-testid="rd-header">
        <div className="rd-header__id">
          <span className="rd-header__title">Dachplaner</span>
          <span className="rd-header__meta">
            {state.importSource != null ? `„${state.importSource.label}"` : "Manueller Entwurf"}
            {savedDesignId != null && ` · v${savedDesignVersion ?? 1} · ${savedDesignStatus ?? "draft"}`}
          </span>
        </div>

        <div className="rd-header__status">
          <span className="rd-chip rd-chip--draft" title="Alle Ergebnisse sind vorläufige Schätzwerte.">
            <span className="rd-chip__dot" />Entwurf
          </span>
          <span className="rd-chip rd-chip--missing" title="canApprove ist strukturell false — KI kann niemals freigeben.">
            <span className="rd-chip__dot" />Nicht freigegeben
          </span>
          <span className="rd-chip rd-chip--review" title="Statik, Elektrik, Wind-/Schneelast: Endprüfung durch Fachkraft zwingend.">
            <span className="rd-chip__dot" />Menschliche Prüfung erforderlich
          </span>
        </div>

        {projectId != null && (
          <div className="rd-header__actions">
            <span className={`rd-savedot ${saveDotClass}`} title={saveLabel} />
            <span className="rd-header__savelabel">{loadSuccessMsg ?? saveLabel}</span>
            <button
              className="rd-btn-primary"
              onClick={() => { void handleSave(); }}
              disabled={saveState === "saving" || saveState === "saved"}
            >
              Speichern
            </button>
            <button className="rd-btn-ghost" onClick={() => { void onLoadFromApi(); }}>
              Laden
            </button>
          </div>
        )}
      </div>

      {/* ── Workflow-Stepbar ── */}
      <StepBar state={state} result={result} dispatch={dispatch} />

      {/* ── Import-Karte ── */}
      {projectId != null && (
        <ImportCard
          surfaces={availableSurfaces}
          selectedId={selectedSurfaceId}
          onSelect={setSelectedSurfaceId}
          onImport={handleImportSurface}
          importSource={state.importSource ?? null}
          satelliteActive={satelliteActive}
        />
      )}

      {/* API-Fehleranzeige */}
      {apiLoadError != null && (
        <div className="rd-error">⚠ {apiLoadError}</div>
      )}

      <Toolbar state={state} result={result} dispatch={dispatch} />
      <div className="rd-main">
        <DesignerCanvas state={state} result={result} dispatch={dispatch} />
        <div className="rd-side">
          <ResultPanel
            state={state}
            result={result}
            hasPolygon={hasPolygon}
            onExport={handleExport}
            onClearSaved={handleClearSaved}
          />
          <PropertiesPanel state={state} dispatch={dispatch} />
        </div>
      </div>
    </div>
  );
}
